// pages/InvoicesPage.tsx - COMPLETE ENHANCED VERSION
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import apiClient from '@/lib/api-client';
import {
 FileText, Plus, Search, X, CheckCircle, Clock, AlertCircle,
 DollarSign, RefreshCw, Ban, Filter, Calendar, Truck,
 CreditCard, Eye, ChevronRight, Building2, Wallet,
 Save
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// TYPES
// ============================================================

interface Invoice {
 id: string;
 invoiceNumber: string;
 vendorId: string;
 vendorName?: string;
 poId: string | null;
 poNumber?: string;
 grnId: string | null;
 grnNumber?: string;
 subtotal: string;
 taxAmount: string;
 totalAmount: string;
 amountPaid: string;
 balanceDue: string;
 invoiceDate: string;
 dueDate: string;
 status: string;
 paymentTerms: string | null;
 notes: string | null;
 createdByName?: string;
 createdAt: string;
}

// ============================================================
// CONSTANTS
// ============================================================

const STATUS_STYLES: Record<string, string> = {
 pending: 'bg-amber-100 text-amber-700 border-amber-200 ',
 approved: 'bg-blue-100 text-blue-700 border-blue-200 ',
 partially_paid: 'bg-purple-100 text-purple-700 border-purple-200 ',
 paid: 'bg-emerald-100 text-emerald-700 border-emerald-200 ',
 disputed: 'bg-rose-100 text-rose-700 border-rose-200 ',
 cancelled: 'bg-slate-100 text-slate-600 border-slate-200 ',
};

const STATUS_ICONS: Record<string, React.ElementType> = {
 pending: Clock,
 approved: CheckCircle,
 partially_paid: AlertCircle,
 paid: CheckCircle,
 disputed: AlertCircle,
 cancelled: Ban,
};

// ============================================================
// MAIN COMPONENT
// ============================================================

export function InvoicesPage() {
 const user = useAuthStore((s) => s.user);
 const navigate = useNavigate();
 const queryClient = useQueryClient();
 const addToast = useToastStore((s) => s.addToast);

 const [search, setSearch] = useState('');
 const [statusFilter, setStatusFilter] = useState('');
 const [showOverdueOnly, setShowOverdueOnly] = useState(false);
 const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
 const [showPaymentModal, setShowPaymentModal] = useState(false);
 const [paymentAmount, setPaymentAmount] = useState('');
 const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
 const [paymentRef, setPaymentRef] = useState('');

 // ============================================================
 // QUERIES
 // ============================================================

 const { data, isLoading, isError, refetch } = useQuery({
 queryKey: ['invoices', user?.tenantId, search, statusFilter, showOverdueOnly],
 queryFn: async () => {
 const params: any = { limit: 50, sortBy: 'createdAt', order: 'desc' };
 if (search) params.search = search;
 if (statusFilter) params.status = statusFilter;
 if (showOverdueOnly) params.overdue = true;
 const { data } = await apiClient.get(`/tenants/${user!.tenantId}/invoices`, { params });
 return data;
 },
 enabled: !!user?.tenantId,
 });

 // Invoice detail with payments
 const { data: invoiceDetail, refetch: refetchDetail } = useQuery({
 queryKey: ['invoice-detail', selectedInvoice?.id],
 queryFn: async () => {
 if (!selectedInvoice?.id) return null;
 const { data } = await apiClient.get(`/tenants/${user!.tenantId}/invoices/${selectedInvoice.id}`);
 return data.data || data;
 },
 enabled: !!selectedInvoice?.id && !!user?.tenantId,
 });

 // ============================================================
 // MUTATIONS
 // ============================================================

 const approveMutation = useMutation({
 mutationFn: (invoiceId: string) => apiClient.post(`/tenants/${user!.tenantId}/invoices/${invoiceId}/approve`),
 onSuccess: () => {
 queryClient.invalidateQueries({ queryKey: ['invoices'] });
 addToast({ type: 'success', title: 'Invoice approved', message: 'The invoice has been approved for payment.' });
 },
 onError: (err: any) => addToast({ type: 'error', title: 'Failed', message: err?.response?.data?.error?.message }),
 });

 const recordPaymentMutation = useMutation({
 mutationFn: () => apiClient.post(`/tenants/${user!.tenantId}/invoices/${selectedInvoice!.id}/payments`, {
 amount: paymentAmount,
 paymentMethod,
 reference: paymentRef || undefined,
 }),
 onSuccess: () => {
 queryClient.invalidateQueries({ queryKey: ['invoices'] });
 refetchDetail();
 setShowPaymentModal(false);
 setPaymentAmount('');
 setPaymentRef('');
 addToast({ type: 'success', title: 'Payment recorded', message: 'Payment has been applied to the invoice.' });
 },
 onError: (err: any) => addToast({ type: 'error', title: 'Failed', message: err?.response?.data?.error?.message }),
 });

 const disputeMutation = useMutation({
 mutationFn: (invoiceId: string) => apiClient.patch(`/tenants/${user!.tenantId}/invoices/${invoiceId}`, { status: 'disputed' }),
 onSuccess: () => {
 queryClient.invalidateQueries({ queryKey: ['invoices'] });
 addToast({ type: 'success', title: 'Invoice disputed', message: 'The invoice has been marked as disputed.' });
 },
 onError: (err: any) => addToast({ type: 'error', title: 'Failed', message: err?.response?.data?.error?.message }),
 });

 // ============================================================
 // HELPERS
 // ============================================================

 const invoices: Invoice[] = data?.data || [];
 const totalOutstanding = invoices
 .filter((i: Invoice) => i.status !== 'paid' && i.status !== 'cancelled')
 .reduce((s: number, i: Invoice) => s + parseFloat(i.balanceDue || '0'), 0);
 const overdueCount = invoices.filter((i: Invoice) =>
 i.status !== 'paid' && i.status !== 'cancelled' && new Date(i.dueDate) < new Date()
 ).length;
 const pendingCount = invoices.filter((i: Invoice) => i.status === 'pending').length;
 const paidCount = invoices.filter((i: Invoice) => i.status === 'paid').length;

 const formatNaira = (val: string | number): string => {
 const num = typeof val === 'string' ? parseFloat(val) : val;
 return isNaN(num) ? '₦0.00' : `₦${num.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
 };

 const formatDate = (d: string | null): string => {
 if (!d) return '—';
 return new Date(d).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' });
 };

 const hasActiveFilters = search || statusFilter || showOverdueOnly;

 return (
 <div className="space-y-5 animate-fade-in max-w-[1600px] mx-auto">
 {/* Header */}
 <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
 <div className="flex items-center gap-3">
 <div className="p-2 rounded-xl bg-gradient-to-br from-teal-500 to-teal-700 shadow-lg shadow-teal-500/20">
 <FileText size={18} className="text-white" />
 </div>
 <div>
 <h1 className="text-xl font-bold text-slate-900 ">Vendor Invoices</h1>
 <p className="text-xs text-slate-500 ">
 {invoices.length} invoices · {formatNaira(totalOutstanding)} outstanding · {overdueCount} overdue
 </p>
 </div>
 </div>
 <div className="flex items-center gap-2">
 <Button variant="ghost" size="sm" onClick={() => refetch()}>
 <RefreshCw size={13} className="mr-1" /> Refresh
 </Button>
 <Button size="sm" onClick={() => navigate('/invoices/new')}>
 <Plus size={14} className="mr-1" /> Record Invoice
 </Button>
 </div>
 </div>

 {/* Stats */}
 <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
 <StatCard label="Total Invoices" value={invoices.length} icon={FileText} gradient="from-slate-500 to-slate-700" />
 <StatCard label="Pending" value={pendingCount} icon={Clock} gradient="from-amber-500 to-orange-600" />
 <StatCard label="Outstanding" value={formatNaira(totalOutstanding)} icon={DollarSign} gradient="from-rose-400 to-rose-600" warning={totalOutstanding > 0} />
 <StatCard label="Overdue" value={String(overdueCount)} icon={AlertCircle} gradient="from-red-500 to-red-700" warning={overdueCount > 0} />
 <StatCard label="Paid" value={String(paidCount)} icon={CheckCircle} gradient="from-emerald-500 to-teal-600" />
 </div>

 {/* Filters */}
 <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-2">
 <div className="flex items-center gap-2 flex-wrap">
 <div className="relative flex-1 min-w-[200px]">
 <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
 <input type="text" placeholder="Search by invoice number..." value={search}
 onChange={(e) => setSearch(e.target.value)}
 className="w-full pl-9 pr-8 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400" />
 {search && <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400"><X size={14} /></button>}
 </div>
 <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
 className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white ">
 <option value="">All Status</option>
 <option value="pending">Pending</option>
 <option value="approved">Approved</option>
 <option value="partially_paid">Partially Paid</option>
 <option value="paid">Paid</option>
 <option value="disputed">Disputed</option>
 </select>
 <button onClick={() => setShowOverdueOnly(!showOverdueOnly)}
 className={clsx('px-3 py-2 rounded-lg text-xs font-medium border transition-all',
 showOverdueOnly ? 'bg-rose-50 text-rose-700 border-rose-200' : 'border-slate-200 text-slate-500 hover:bg-slate-50')}>
 Overdue Only
 </button>
 {hasActiveFilters && (
 <button onClick={() => { setSearch(''); setStatusFilter(''); setShowOverdueOnly(false); }}
 className="text-xs text-teal-600 font-medium">Clear all</button>
 )}
 </div>
 </div>

 {/* Content */}
 {isLoading ? (
 <div className="flex justify-center py-20"><Spinner size="xl" /></div>
 ) : isError ? (
 <ErrorState title="Failed to load invoices" message="There was a problem loading your invoices." onRetry={() => refetch()} />
 ) : invoices.length === 0 ? (
 <EmptyState icon={<FileText size={48} />}
 title={hasActiveFilters ? "No matching invoices" : "No invoices yet"}
 description={hasActiveFilters ? "Try adjusting your filters." : "Record vendor invoices to track what you owe. Invoices can also be auto-generated from confirmed GRNs."}
 action={!hasActiveFilters ? { label: 'Record Invoice', onClick: () => navigate('/invoices/new') } : undefined} />
 ) : (
 <Card className="overflow-hidden">
 <div className="overflow-x-auto">
 <table className="min-w-full divide-y divide-slate-100 ">
 <thead><tr className="bg-slate-50/50 ">
 <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase">Invoice #</th>
 <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase hidden md:table-cell">Vendor</th>
 <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase hidden lg:table-cell">Linked PO</th>
 <th className="px-4 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase">Amount</th>
 <th className="px-4 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase hidden sm:table-cell">Balance</th>
 <th className="px-4 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase">Status</th>
 <th className="px-4 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase hidden sm:table-cell">Due Date</th>
 <th className="px-4 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase">Actions</th>
 </tr></thead>
 <tbody className="divide-y divide-slate-50 ">
 {invoices.map((inv: Invoice) => {
 const StatusIcon = STATUS_ICONS[inv.status] || Clock;
 const isOverdue = inv.status !== 'paid' && inv.status !== 'cancelled' && new Date(inv.dueDate) < new Date();
 const isPaid = inv.status === 'paid';

 return (
 <tr key={inv.id}
 className="group hover:bg-slate-50 :bg-slate-800/50 cursor-pointer transition-colors"
 onClick={() => setSelectedInvoice(inv)}>
 <td className="px-4 py-3">
 <p className="text-sm font-mono font-medium text-slate-800 ">{inv.invoiceNumber}</p>
 <p className="text-xs text-slate-400">{formatDate(inv.invoiceDate)}</p>
 </td>
 <td className="px-4 py-3 hidden md:table-cell text-sm text-slate-600 ">
 {inv.vendorName || '—'}
 </td>
 <td className="px-4 py-3 hidden lg:table-cell">
 {inv.poNumber ? (
 <span className="text-xs font-mono text-teal-600 ">{inv.poNumber}</span>
 ) : (
 <span className="text-xs text-slate-400">Manual</span>
 )}
 </td>
 <td className="px-4 py-3 text-right text-sm font-semibold text-slate-800 ">
 {formatNaira(inv.totalAmount)}
 </td>
 <td className="px-4 py-3 text-right hidden sm:table-cell">
 <span className={clsx('text-sm font-semibold', isPaid ? 'text-slate-400' : 'text-rose-600')}>
 {isPaid ? '—' : formatNaira(inv.balanceDue)}
 </span>
 </td>
 <td className="px-4 py-3 text-center">
 <span className={clsx('inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full border', STATUS_STYLES[inv.status] || '')}>
 <StatusIcon size={11} />
 {inv.status?.replace(/_/g, ' ')}
 </span>
 </td>
 <td className={clsx('px-4 py-3 text-right hidden sm:table-cell text-sm',
 isOverdue ? 'text-rose-500 font-semibold' : 'text-slate-500')}>
 {formatDate(inv.dueDate)}
 {isOverdue && <span className="text-[10px] block text-rose-400">Overdue</span>}
 </td>
 <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
 <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
 {inv.status === 'pending' && (
 <Button size="xs" onClick={() => approveMutation.mutate(inv.id)} isLoading={approveMutation.isPending}>
 <CheckCircle size={11} className="mr-1" /> Approve
 </Button>
 )}
 {(inv.status === 'approved' || inv.status === 'partially_paid') && (
 <Button size="xs" variant="secondary"
 onClick={() => { setSelectedInvoice(inv); setShowPaymentModal(true); setPaymentAmount(inv.balanceDue); }}>
 <Wallet size={11} className="mr-1" /> Pay
 </Button>
 )}
 {inv.status === 'pending' && (
 <Button size="xs" variant="ghost" onClick={() => disputeMutation.mutate(inv.id)}>
 <AlertCircle size={11} className="mr-1" /> Dispute
 </Button>
 )}
 <Button size="xs" variant="ghost" onClick={() => setSelectedInvoice(inv)}>
 <Eye size={13} />
 </Button>
 </div>
 </td>
 </tr>
 );
 })}
 </tbody>
 </table>
 </div>
 </Card>
 )}

 {/* Invoice Detail Panel */}
 <AnimatePresence>
 {selectedInvoice && (
 <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
 className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
 <motion.div initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 20 }}
 className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">

 <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-4 text-white flex items-center justify-between flex-shrink-0">
 <div className="flex items-center gap-3">
 <div className="p-1.5 rounded-lg bg-white/20"><FileText size={18} className="text-white" /></div>
 <div>
 <h2 className="text-lg font-bold">{selectedInvoice.invoiceNumber}</h2>
 <p className="text-teal-100 text-xs">{selectedInvoice.vendorName || 'Unknown Vendor'}</p>
 </div>
 </div>
 <button onClick={() => setSelectedInvoice(null)} className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30"><X size={16} className="text-white" /></button>
 </div>

 <div className="flex-1 overflow-y-auto p-6 space-y-4">
 {/* Status & Amount */}
 <div className="grid grid-cols-2 gap-3">
 <div className="p-4 bg-slate-50 rounded-xl">
 <p className="text-xs text-slate-400">Status</p>
 <span className={clsx('inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full border mt-1', STATUS_STYLES[selectedInvoice.status] || '')}>
 {selectedInvoice.status?.replace(/_/g, ' ')}
 </span>
 </div>
 <div className="p-4 bg-slate-50 rounded-xl">
 <p className="text-xs text-slate-400">Total Amount</p>
 <p className="text-xl font-bold">{formatNaira(selectedInvoice.totalAmount)}</p>
 </div>
 <div className="p-4 bg-slate-50 rounded-xl">
 <p className="text-xs text-slate-400">Amount Paid</p>
 <p className="text-lg font-bold text-emerald-600">{formatNaira(selectedInvoice.amountPaid)}</p>
 </div>
 <div className="p-4 bg-slate-50 rounded-xl">
 <p className="text-xs text-slate-400">Balance Due</p>
 <p className="text-lg font-bold text-rose-600">{formatNaira(selectedInvoice.balanceDue)}</p>
 </div>
 </div>

 {/* Dates */}
 <div className="grid grid-cols-2 gap-3 text-sm">
 <div><span className="text-slate-400">Invoice Date:</span> <span className="font-medium">{formatDate(selectedInvoice.invoiceDate)}</span></div>
 <div><span className="text-slate-400">Due Date:</span> <span className={clsx('font-medium', isOverdue(selectedInvoice) ? 'text-rose-500' : '')}>{formatDate(selectedInvoice.dueDate)}</span></div>
 <div><span className="text-slate-400">Payment Terms:</span> <span className="font-medium">{selectedInvoice.paymentTerms || '—'}</span></div>
 <div><span className="text-slate-400">Linked PO:</span> <span className="font-medium">{selectedInvoice.poNumber || 'Manual'}</span></div>
 </div>

 {/* Quick Actions */}
 <div className="flex gap-2">
 {selectedInvoice.status === 'pending' && (
 <Button size="sm" className="flex-1" onClick={() => { approveMutation.mutate(selectedInvoice.id); setSelectedInvoice(null); }}>
 <CheckCircle size={14} className="mr-1" /> Approve
 </Button>
 )}
 {(selectedInvoice.status === 'approved' || selectedInvoice.status === 'partially_paid') && (
 <Button size="sm" className="flex-1"
 onClick={() => { setShowPaymentModal(true); setPaymentAmount(selectedInvoice.balanceDue); }}>
 <Wallet size={14} className="mr-1" /> Record Payment
 </Button>
 )}
 </div>

 {/* Notes */}
 {selectedInvoice.notes && (
 <div className="p-3 bg-slate-50 rounded-xl">
 <p className="text-xs text-slate-400 mb-1">Notes</p>
 <p className="text-sm">{selectedInvoice.notes}</p>
 </div>
 )}

 {/* Creator Info */}
 <div className="text-xs text-slate-400 flex items-center gap-1">
 <Clock size={10} />
 Created by {selectedInvoice.createdByName || 'System'} on {formatDate(selectedInvoice.createdAt)}
 </div>
 </div>
 </motion.div>
 </motion.div>
 )}
 </AnimatePresence>

 {/* Payment Modal */}
 <AnimatePresence>
 {showPaymentModal && selectedInvoice && (
 <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
 className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
 <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
 className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
 <div className="flex items-center justify-between mb-4">
 <h3 className="text-lg font-bold">Record Payment</h3>
 <button onClick={() => setShowPaymentModal(false)} className="p-1 rounded-lg hover:bg-slate-100"><X size={18} /></button>
 </div>
 <div className="space-y-4">
 <div className="p-3 bg-slate-50 rounded-xl">
 <p className="text-xs text-slate-400">Invoice</p>
 <p className="font-semibold">{selectedInvoice.invoiceNumber}</p>
 <p className="text-sm text-rose-600 mt-1">Balance Due: {formatNaira(selectedInvoice.balanceDue)}</p>
 </div>
 <Input label="Amount *" type="number" step="0.01" value={paymentAmount}
 onChange={(e) => setPaymentAmount(e.target.value)} required />
 <div>
 <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Payment Method</label>
 <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}
 className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-xl text-sm bg-white ">
 <option value="bank_transfer">Bank Transfer</option>
 <option value="cash">Cash</option>
 <option value="cheque">Cheque</option>
 </select>
 </div>
 <Input label="Reference" value={paymentRef} onChange={(e) => setPaymentRef(e.target.value)} placeholder="Transaction reference" />
 <Button className="w-full" onClick={() => recordPaymentMutation.mutate()} isLoading={recordPaymentMutation.isPending}
 disabled={!paymentAmount || parseFloat(paymentAmount) <= 0}>
 <Save size={16} className="mr-1.5" /> Record Payment
 </Button>
 </div>
 </motion.div>
 </motion.div>
 )}
 </AnimatePresence>
 </div>
 );
}

// Helper
const isOverdue = (inv: Invoice): boolean => {
 return inv.status !== 'paid' && inv.status !== 'cancelled' && new Date(inv.dueDate) < new Date();
};

// Stat Card
const StatCard: React.FC<{ label: string; value: string | number; icon: React.ElementType; gradient: string; warning?: boolean }> = ({ label, value, icon: Icon, gradient, warning }) => (
 <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} whileHover={{ y: -2 }}
 className={clsx('bg-white rounded-xl border shadow-sm p-4 hover:shadow-md transition-all',
 warning ? 'border-amber-300 ' : 'border-slate-200 ')}>
 <div className="flex items-center justify-between">
 <div><p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{label}</p><p className="text-xl font-bold mt-0.5">{value}</p></div>
 <div className={`p-2.5 rounded-lg bg-gradient-to-br ${gradient} shadow-md`}><Icon className="w-4 h-4 text-white" /></div>
 </div>
 </motion.div>
);